// @flow
import * as React from 'react';
import { times, noop } from 'lodash';
import Loader from 'app/components/commons/Loader';
import { getPassiveIfSupported } from '../../utils/domUtils';

// [MC] Progressive load list items. By default it loads X rows first, then
// we you scroll to the end of that list it requests to load more (displaying a loader at the end
// of the list by default).
//
// You can also choose to lazily load more rows on each idle frame.
//
// This results in a fast initial render and avoids the complicated nature of a virtualised list.
//
// NOTE - This is a pure component so be sure to pass in new props if you need the list to update.
// By default if the length prop doesn't change then you won't see a re-render.
//
// NOTE - requestIdleCallback is currently not supported by safari.

type Props = {
  className?: string,
  disabled?: boolean,
  idleAmount?: number,
  initialAmount?: number,
  itemRenderer: (index: number) => any,
  length: number,
  minActiveThreshold?: number, // we don't want to bother progressive loading if length is very close to initialAmount
  progressiveAmount?: number,
  renderLoader?: () => any
};

type State = {
  numRenderRows: number
};

const passive = getPassiveIfSupported();

class ProgressiveRenderList extends React.PureComponent<Props, State> {
  props: Props;

  state: State;

  requestId: number; // eslint-disable-line react/sort-comp

  ref: React.Node; // eslint-disable-line react/sort-comp

  isLoading = false; // eslint-disable-line react/sort-comp

  static defaultProps = {
    className: undefined,
    disabled: false,
    idleAmount: 0, // load one extra row on idle by default
    initialAmount: 20,
    minActiveThreshold: 40,
    progressiveAmount: 40,
    renderLoader: () => (
      <div style={{ paddingTop: 10, paddingBottom: 30 }}>
        <Loader />
      </div>
    )
  };

  constructor(props: Props, ...args: Array<any>) {
    super(props, ...args);
    const { length, initialAmount, minActiveThreshold } = props;
    this.state = {
      numRenderRows: length <= minActiveThreshold ? length : initialAmount
    };
  }

  componentDidMount() {
    window.addEventListener('scroll', this.handleScroll, passive);
  }

  handleScroll = () => {
    const { length, progressiveAmount } = this.props;
    const { numRenderRows } = this.state;
    const { top, height } = this.ref.getBoundingClientRect();
    const threshold = 20; // request more frames when you get within 20 pixels of the bottom

    if (
      top + height <= window.innerHeight + threshold &&
      numRenderRows !== length &&
      !this.isLoading
    ) {
      this.loadMore(progressiveAmount);
    }
  };

  UNSAFE_componentWillReceiveProps(nextProps: Props) {
    const { length } = this.props;

    if (nextProps.length !== length) {
      this.initializeList(nextProps);
    }
  }

  componentWillUnmount() {
    if (window.requestIdleCallback) {
      window.cancelIdleCallback(this.requestId);
    }

    window.removeEventListener('scroll', this.handleScroll, passive);
  }

  initializeList(props: Props) {
    const { length, minActiveThreshold, initialAmount } = props;

    if (window.requestIdleCallback) {
      window.cancelIdleCallback(this.requestId);
    }

    this.setState({ numRenderRows: length <= minActiveThreshold ? length : initialAmount }, () => {
      // this.progressivelyLoadMore(false);
    });
  }

  progressivelyLoadMore = (immediateLoad: boolean = true) => {
    if (!window.requestIdleCallback) return;
    const { length, idleAmount } = this.props;
    const { numRenderRows } = this.state;
    if (immediateLoad) this.loadMore(idleAmount);
    if (numRenderRows < length) {
      this.requestId = window.requestIdleCallback(this.progressivelyLoadMore);
    }
  };

  loadMore(amount: number) {
    const { length } = this.props;
    const { numRenderRows } = this.state;
    if (numRenderRows >= length) {
      return;
    }

    this.isLoading = true;
    this.setState(
      state => ({
        numRenderRows: Math.min(state.numRenderRows + amount, length)
      }),
      () => {
        this.isLoading = false;
      }
    );
  }

  loadAll() {
    const { length } = this.props;
    this.isLoading = true;
    this.setState(
      () => ({
        numRenderRows: length
      }),
      () => {
        this.isLoading = false;
      }
    );
  }

  getNumRendered() {
    const { numRenderRows } = this.state;

    return numRenderRows;
  }

  render() {
    const { disabled, className, itemRenderer, length, renderLoader = noop } = this.props;
    const { numRenderRows } = this.state;

    return (
      <div
        ref={ref => {
          this.ref = ref;
        }}
        className={className}
      >
        {disabled ? (
          <React.Fragment>{times(length, i => itemRenderer(i))}</React.Fragment>
        ) : (
          <React.Fragment>
            {times(numRenderRows, i => itemRenderer(i))}
            {numRenderRows < length && renderLoader()}
          </React.Fragment>
        )}
      </div>
    );
  }
}

export default ProgressiveRenderList;
